import { 
  Flex, 
  Box, 
  Text,
  SliderTrack,
  SliderThumb,
  Slider as ChakraSlider,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react"
import { metricIcon } from "./StatSlider"
import { IconInfo, IconMedal, IconTrophyFilled } from "icons"
import CustomTooltip from "components/Tooltip/CustomTooltip"
import { formatOrdinal } from "utils/helpers"

// Define the getTextColor function since it's not exported from StatSlider
const getTextColor = (value: number) => {
  if (value >= 0 && value <= 3.9) return 'bloodMoon.100';
  if (value >= 6.1 && value <= 10) return 'treasuredTeal.100';
  return 'firstDaffodil.100';
}

interface FeedbackSliderProps {
  statName: string;
  statField: string;
  userValue: number;
  winnerMinValue: number;
  winnerMaxValue: number;
  min?: number;
  max?: number;
  tip?: string | null;
  userWon?: boolean;
  position?: number;
  competitorPosition?: number;
}

const FeedbackSlider = ({
  statName,
  statField,
  userValue,
  winnerMinValue,
  winnerMaxValue,
  min = 0,
  max = 10,
  tip,
  userWon,
  position,
  competitorPosition
}: FeedbackSliderProps) => {
  // Ensure min is always smaller than max
  const normalizedWinnerMin = Math.min(winnerMinValue, winnerMaxValue);
  const normalizedWinnerMax = Math.max(winnerMinValue, winnerMaxValue);
  
  // Clamp the winner min/max values to ensure they stay within slider boundaries
  const clampedWinnerMin = Math.max(min, Math.min(normalizedWinnerMin, max));
  const clampedWinnerMax = Math.max(min, Math.min(normalizedWinnerMax, max));
  
  // Calculate the width and position of the winner's tuning area
  const winnerAreaWidth = ((clampedWinnerMax - clampedWinnerMin) / (max - min)) * 100;
  const winnerAreaLeft = ((clampedWinnerMin - min) / (max - min)) * 100;

  // Setup tooltip disclosure
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <Flex 
      direction="column" 
      width="full"
      p="4"
      borderRadius="8px"
      gap="5"
    >
      <Flex align="center" mb={2} gap="2">
        <Box>
          {metricIcon(statField)}
        </Box>
        <Text
          fontSize="16px"
          lineHeight="24px"
          fontWeight="400"
          fontFamily="heading"
          textTransform="uppercase"
          color="white.95"
        >
          {statName}
        </Text>
      </Flex>
      <Flex align="center" gap={{ base: 4, md: 10 }}>
        <Box position="relative" flex={1} alignItems="center" display="flex">
          <ChakraSlider
            aria-label={`${statName}-slider`}
            min={min}
            max={max}
            step={0.1}
            value={userValue}
            isReadOnly={true}
          >
            {/* Winner's tuning area */}
            {winnerAreaWidth > 0 && (
              <Flex
                position="absolute"
                h="full"
                alignItems="flex-start"
                justifyContent="center"
                style={{
                  width: `${winnerAreaWidth}%`,
                  left: `${winnerAreaLeft}%`,
                  height: `42px`,
                  transform: 'translateX(0)',
                  zIndex: 100
                }}
                background={
                  userWon ? 
                  "linear-gradient(180deg, rgba(255, 255, 255, 0.24) 0%, rgba(255, 255, 255, 0) 100%)" 
                  : "linear-gradient(180deg, rgba(255, 221, 86, 0.24) 0%, rgba(255, 221, 86, 0) 100%)"
                }
                borderTopRadius="4px"
                opacity={1}
                top="-6"
                zIndex="100"
                gap="0.25rem"
                pointerEvents="none"
              >
                <Text
                  fontSize="12px"
                  lineHeight="16px"
                  fontWeight="600"
                  mt="0.25rem"
                  color={!userWon ? 'rgba(255, 221, 86, 1)' : 'rgba(255, 255, 255, 1)'}
                >
                  {formatOrdinal(competitorPosition || 0)}
                </Text>
                {tip && (
                  <CustomTooltip
                    isOpen={isOpen && !!tip}
                    label={tip}
                  >
                    <Box 
                      onMouseEnter={onOpen}
                      onMouseLeave={onClose}
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpen();
                      }}
                      cursor={tip ? "pointer" : "default"}
                      pointerEvents="all"
                    >
                      <IconInfo 
                        w="0.75rem"
                        h="auto"
                        mt="0.25rem"
                        __css={{
                          path: {
                            fill: !userWon ? 'rgba(255, 221, 86, 1)' : 'rgba(255, 255, 255, 1)'
                          },
                        }}
                      />
                    </Box>
                  </CustomTooltip>
                )}
              </Flex>
            )}
            <SliderTrack
              background="rgba(51, 51, 51, 1)"
              h="0.85rem"
              borderRadius="0.25rem"
              position="relative"
            >
              {userValue > 5 && (
                <Box 
                  position="absolute"
                  top="0"
                  left="50%"
                  right="0"
                  bottom="0"
                  background="linear-gradient(90deg, rgba(15, 64, 59, 0) 0%, #26A69A 100%)"
                />
              )}
              {userValue < 5 && (
                <Box 
                  position="absolute"
                  top="0"
                  left="0"
                  right="50%"
                  bottom="0"
                  background="linear-gradient(90deg, #D83832 0%, rgba(114, 30, 26, 0) 100%)"
                />
              )}
            </SliderTrack>

            <SliderThumb
              w="38px"
              h="38px"
              background="linear-gradient(360deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0) 100%), linear-gradient(0deg, #000000, #000000)"
              style={{
                backgroundOrigin: 'border-box',
              }}
              border={!userWon 
                ? "1.5px solid rgba(0, 255, 232, 0.6)"
                : "1.5px solid rgba(255, 242, 0, 0.6)"}
              borderRadius="8px"
              p="0"
            >
              <Flex
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
              >
                <Text
                  fontSize="16px"
                  lineHeight="18px"
                  fontWeight="600"
                  color={getTextColor(userValue)}
                >
                  {Number.isInteger(userValue) ? userValue.toString() : userValue.toFixed(2)}
                </Text>
                <Text
                  fontSize="8px"
                  lineHeight="8px"
                  fontWeight="400"
                  color={userWon ? 'rgba(255, 242, 0, 1)' : 'rgba(0, 255, 232, 1)'}
                >
                  {formatOrdinal(position || 0)}
                </Text>
              </Flex>
            </SliderThumb>
          </ChakraSlider>
        </Box>
      </Flex>
    </Flex>
  )
}

export default FeedbackSlider
